import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw, Volume2, VolumeX, Phone, MessageSquare, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SCENE_COMPONENTS, DEMO_SCENES } from "@/pages/HowItWorksPage";
import axios from "axios";

/**
 * Public /demo route — no auth, no dashboard chrome. This is the URL James
 * texts or emails to a dealer principal who missed the meeting. Same
 * animation, same ka-ching, minimal footer.
 */
export default function PublicDemoPage() {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [contact, setContact] = useState({ owner_name: "James Ayers", owner_phone: "", owner_email: "" });
  const kachingRef = useRef(null);
  const current = DEMO_SCENES[idx];

  useEffect(() => {
    // Public endpoint — no auth. Fetch owner contact for the tap-to-call buttons.
    const base = process.env.REACT_APP_BACKEND_URL || "";
    axios.get(`${base}/api/public/contact`)
      .then((r) => setContact(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    try {
      const a = new Audio("/kaching.wav");
      a.preload = "auto";
      a.volume = 1.0;
      kachingRef.current = a;
    } catch (_) {}
  }, []);

  const playKaching = () => {
    const a = kachingRef.current;
    if (!a) return;
    try { a.pause(); a.currentTime = 0; a.play().catch(() => {}); } catch (_) {}
  };

  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => setIdx((i) => (i + 1) % DEMO_SCENES.length), current.duration);
    return () => clearTimeout(t);
  }, [idx, playing, current.duration]);

  useEffect(() => {
    if (muted || !audioReady) return;
    if (current.key === "booked") setTimeout(() => playKaching(), 550);
    if (current.key === "recovered") setTimeout(() => playKaching(), 100);
  }, [idx, muted, audioReady, current.key]);

  const Scene = SCENE_COMPONENTS[current.key];

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex flex-col">
      <div className="bg-navy text-white text-center py-3 flex items-center justify-center gap-3 border-b border-[hsl(var(--gold))]/30">
        <img src="/logo-brand.png" alt="Decline Drive" className="h-10 w-auto object-contain" />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-4xl">
          <div className="relative overflow-hidden rounded-lg border border-[hsl(var(--gold))]/40 shadow-2xl">
            <div className="relative bg-gradient-to-br from-navy via-[#0a1d4a] to-[#050f2b] h-[540px] md:h-[680px]">
              <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.55) 100%)" }} />
              <div className="absolute inset-x-0 top-0 z-10 px-5 pt-5 pb-3 pointer-events-none">
                <AnimatePresence mode="wait">
                  <motion.div key={`cap-${current.key}`} initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.4 }} className="flex justify-center">
                    <div className="inline-block px-7 py-3.5 rounded-full bg-neutral-950/90 border border-[hsl(var(--gold))]/50 shadow-2xl max-w-3xl" style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}>
                      <div className="font-display font-black text-xl md:text-3xl tracking-tighter text-center leading-tight" style={{ color: "#FFD25F", textShadow: "0 0 20px rgba(255,210,95,0.4), 0 2px 4px rgba(0,0,0,0.5)" }}>
                        {current.caption}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
              <AnimatePresence mode="wait">
                <motion.div key={current.key} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="absolute inset-0">
                  <Scene />
                </motion.div>
              </AnimatePresence>
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 to-transparent">
                <div className="flex gap-1.5">
                  {DEMO_SCENES.map((s, i) => (
                    <div key={s.key} className="h-1 flex-1 rounded-full bg-white/15 overflow-hidden">
                      {i === idx && playing && (
                        <motion.div key={`bar-${idx}`} initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: current.duration / 1000, ease: "linear" }} className="h-full bg-[hsl(var(--gold))]" />
                      )}
                      {i < idx && <div className="h-full w-full bg-[hsl(var(--gold))]/60" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            <Button onClick={() => { setAudioReady(true); setMuted((m) => !m); if (muted) playKaching(); }} className="bg-navy text-white border-2 border-[hsl(var(--gold))]" data-testid="demo-mute">
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </Button>
            <Button onClick={() => { setAudioReady(true); setPlaying((p) => !p); }} className="bg-[hsl(var(--gold))] text-neutral-900 font-bold" data-testid="demo-play">
              {playing ? <><Pause className="w-4 h-4 mr-2" />Pause</> : <><Play className="w-4 h-4 mr-2" />Play</>}
            </Button>
            <Button onClick={() => { setAudioReady(true); setIdx(0); setPlaying(true); }} className="bg-navy text-white border-2 border-[hsl(var(--gold))]" data-testid="demo-replay">
              <RotateCcw className="w-4 h-4 mr-2" />Replay
            </Button>
          </div>

          <div className="mt-8 max-w-lg mx-auto" data-testid="demo-cta">
            <div className="text-center mb-4">
              <div className="font-display font-black text-xl md:text-2xl text-[hsl(var(--gold-light))] tracking-tight">
                Ready to stop watching declines walk out?
              </div>
              <div className="text-white/70 text-sm mt-1">
                Tap below — I'll pick up. — {contact.owner_name}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {contact.owner_phone && (
                <a
                  href={`tel:${contact.owner_phone.replace(/[^0-9+]/g, "")}`}
                  data-testid="cta-call"
                  className="flex items-center justify-center gap-2 bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold-light))] text-neutral-900 font-black rounded-lg px-4 py-4 transition-all shadow-lg active:scale-95"
                >
                  <Phone className="w-5 h-5" strokeWidth={2.5} />
                  Call
                </a>
              )}
              {contact.owner_phone && (
                <a
                  href={`sms:${contact.owner_phone.replace(/[^0-9+]/g, "")}?body=${encodeURIComponent("Interested in Decline Drive after seeing the demo")}`}
                  data-testid="cta-text"
                  className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-black rounded-lg px-4 py-4 transition-all shadow-lg active:scale-95"
                >
                  <MessageSquare className="w-5 h-5" strokeWidth={2.5} />
                  Text
                </a>
              )}
              {contact.owner_email && (
                <a
                  href={`mailto:${contact.owner_email}?subject=${encodeURIComponent("Decline Drive — saw your demo")}`}
                  data-testid="cta-email"
                  className="flex items-center justify-center gap-2 bg-navy hover:bg-[#0a1d4a] text-white font-black rounded-lg px-4 py-4 transition-all shadow-lg border-2 border-[hsl(var(--gold))]/40 active:scale-95"
                >
                  <Mail className="w-5 h-5" strokeWidth={2.5} />
                  Email
                </a>
              )}
            </div>
            <div className="text-center mt-4 font-mono-label text-white/50 text-[0.65rem] tracking-widest">
              {contact.owner_phone} · {contact.owner_email}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-navy text-white/60 text-[0.6rem] font-mono-label text-center py-2 border-t border-white/10">
        © {new Date().getFullYear()} Decline Drive™ by Ayers Enterprises
      </div>
    </div>
  );
}
